import React, { useMemo, useState } from 'react';
import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { ArrowsLeftRight, CalendarX, Key, Equals, WarningCircle } from 'phosphor-react';
import { INotaDuplicada } from '../../../../hooks/useNotasDuplicadas';
import { formatCPFOrCNPJ } from '../../../../utils/FormatFunctions';
import {
  CampoComparado,
  IGrupoDuplicada,
  IPeriodo,
  MotivoDuplicada,
  ROTULO_CAMPO,
  ROTULO_MOTIVO,
  dataDaNota,
  destoa,
  montarGrupos,
  noPeriodo,
  provavelCancelada,
  resumoDosGrupos,
  valorDaNota,
} from './regrasDuplicadas';

const fmtData = (d: string | null) => {
  if (!d) return '—';
  const [y, m, dia] = d.slice(0, 10).split('-');
  return `${dia}/${m}/${y}`;
};

const fmtValor = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const COR_MOTIVO: Record<MotivoDuplicada, { cor: string; bg: string }> = {
  valor: { cor: '#1d4ed8', bg: 'rgba(59,130,246,0.1)' },
  chave: { cor: '#6d28d9', bg: 'rgba(124,58,237,0.1)' },
};

const DICA_MOTIVO: Record<MotivoDuplicada, string> = {
  valor:
    'Duas ou mais notas com o mesmo número, o mesmo CNPJ do cliente/fornecedor e o mesmo valor.',
  chave:
    'A mesma chave eletrônica (ou código de verificação) lançada mais de uma vez para o mesmo cliente/fornecedor. Pega a nota importada duas vezes com série, valor ou situação diferentes, como um lançamento cancelado a R$ 0 e outro ativo.',
};

const Pilula = ({
  children,
  cor,
  bg,
  dica,
}: {
  children: React.ReactNode;
  cor: string;
  bg: string;
  dica?: string;
}) => (
  <Tooltip label={dica} isDisabled={!dica} fontSize="11px" hasArrow maxW="300px" placement="top">
    <Flex
      as="span"
      align="center"
      gap="4px"
      px="8px"
      py="3px"
      borderRadius="999px"
      bg={bg}
      color={cor}
      fontSize="10.5px"
      fontWeight={700}
      whiteSpace="nowrap"
      cursor={dica ? 'help' : undefined}
    >
      {children}
    </Flex>
  </Tooltip>
);

const COLUNAS = '92px minmax(110px, 1fr) 54px 92px 92px 118px';

const Celula = ({
  children,
  muda,
  alinhar = 'left',
  mono = false,
}: {
  children: React.ReactNode;
  muda?: boolean;
  alinhar?: 'left' | 'right';
  mono?: boolean;
}) => (
  <Box
    px="8px"
    py="6px"
    borderRadius="8px"
    textAlign={alinhar}
    fontFamily={mono ? 'mono' : undefined}
    fontSize="12px"
    fontWeight={muda ? 700 : 500}
    color={muda ? '#92400e' : '#334155'}
    bg={muda ? 'rgba(245,158,11,0.14)' : undefined}
    whiteSpace="nowrap"
    overflow="hidden"
    textOverflow="ellipsis"
  >
    {children}
  </Box>
);

const CartaoDoGrupo = ({ g, periodo }: { g: IGrupoDuplicada; periodo?: IPeriodo }) => {
  const m = COR_MOTIVO[g.motivo];
  const numeros = Array.from(new Set(g.notas.map(n => n.numero_nota)));
  const muda = (n: INotaDuplicada, c: CampoComparado) => destoa(n, c, g);
  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="rgba(15,23,42,0.08)"
      borderLeft="3px solid"
      borderLeftColor={m.cor}
      borderRadius="16px"
      boxShadow="0 4px 14px -8px rgba(15,23,42,0.12)"
      p="14px 16px"
    >
      <Flex justify="space-between" align="flex-start" gap={3} wrap="wrap">
        <Box minW={0} flex="1 1 240px">
          <Flex align="baseline" gap={2} wrap="wrap">
            <Text fontSize="15px" fontWeight={700} color="#0F172A" fontFamily="mono">
              Nota {numeros.join(' / ')}
            </Text>
            <Text fontSize="11.5px" color="#64748B">
              {g.notas.length} lançamentos
            </Text>
          </Flex>
          <Text fontSize="12px" color="#475569" noOfLines={1} title={g.contraparte ?? ''}>
            {g.contraparte || '—'}
            {g.cnpj && (
              <Text as="span" color="#94A3B8" fontFamily="mono">
                {' · '}
                {formatCPFOrCNPJ(g.cnpj)}
              </Text>
            )}
          </Text>
        </Box>
        <Flex gap="6px" wrap="wrap" justify="flex-end">
          <Pilula cor={m.cor} bg={m.bg} dica={DICA_MOTIVO[g.motivo]}>
            {g.motivo === 'chave' ? (
              <Key size={11} weight="bold" />
            ) : (
              <Equals size={11} weight="bold" />
            )}
            {ROTULO_MOTIVO[g.motivo]}
          </Pilula>
          {g.tipo && (
            <Pilula
              cor={g.tipo === 'E' ? '#1d4ed8' : '#047857'}
              bg={g.tipo === 'E' ? 'rgba(59,130,246,0.08)' : 'rgba(16,185,129,0.1)'}
            >
              {g.tipo === 'E' ? 'Entrada' : 'Saída'}
            </Pilula>
          )}
        </Flex>
      </Flex>

      {g.canceladaEAtiva && (
        <Flex
          align="flex-start"
          gap="6px"
          mt="10px"
          px="10px"
          py="7px"
          borderRadius="10px"
          bg="rgba(239,68,68,0.08)"
          color="#b91c1c"
          fontSize="12px"
          fontWeight={600}
        >
          <WarningCircle size={15} weight="fill" style={{ flex: 'none', marginTop: 1 }} />
          Um lançamento está a R$ 0 (provável cancelada) e outro continua com valor no Domínio.
          Confira se o lançamento com valor deveria existir.
        </Flex>
      )}

      <Flex gap={3} wrap="wrap" mt="8px" fontSize="11.5px">
        {g.diferencas.length > 0 ? (
          <Flex align="center" gap="5px" color="#92400e">
            <ArrowsLeftRight size={13} weight="bold" />
            Muda entre os lançamentos: {g.diferencas.map(c => ROTULO_CAMPO[c]).join(', ')}
          </Flex>
        ) : (
          <Flex align="center" gap="5px" color="#64748B">
            <Equals size={13} weight="bold" />
            Lançamentos idênticos
          </Flex>
        )}
        {g.foraDoPeriodo > 0 && (
          <Flex align="center" gap="5px" color="#64748B">
            <CalendarX size={13} weight="bold" />
            {g.foraDoPeriodo === 1
              ? '1 lançamento fora do mês filtrado'
              : `${g.foraDoPeriodo} lançamentos fora do mês filtrado`}
          </Flex>
        )}
        {g.chave && (
          <Tooltip label={g.chave} fontSize="11px" hasArrow placement="top">
            <Text color="#94A3B8" fontFamily="mono" noOfLines={1} maxW="260px" cursor="help">
              chave {g.chave}
            </Text>
          </Tooltip>
        )}
      </Flex>

      <Box overflowX="auto" mt="10px">
        <Box minW="580px">
          <Box
            display="grid"
            gridTemplateColumns={COLUNAS}
            gap="2px"
            px="2px"
            pb="4px"
            borderBottom="1px solid"
            borderColor="rgba(15,23,42,0.06)"
          >
            {['Lançamento', 'Cliente/fornecedor', 'Série', 'Nº nota', 'Data', 'Valor'].map(
              (t, i) => (
                <Text
                  key={t}
                  px="8px"
                  fontSize="9.5px"
                  fontWeight={700}
                  color="#94A3B8"
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                  textAlign={i === 5 ? 'right' : 'left'}
                >
                  {t}
                </Text>
              ),
            )}
          </Box>
          {g.notas.map(n => {
            const fora = !noPeriodo(n, periodo);
            const valor = valorDaNota(n);
            return (
              <Box
                key={n.id}
                display="grid"
                gridTemplateColumns={COLUNAS}
                gap="2px"
                px="2px"
                py="2px"
                opacity={fora ? 0.72 : 1}
              >
                <Celula mono>
                  {n.codi_nota}
                  {fora && (
                    <Text as="span" ml="4px" fontFamily="body" fontSize="9.5px" color="#94A3B8">
                      fora do mês
                    </Text>
                  )}
                </Celula>
                <Celula muda={muda(n, 'cadastro')}>
                  <Tooltip label={n.nome_clifor || ''} fontSize="11px" hasArrow placement="top">
                    <span>
                      cód. {n.codi_clifor ?? '—'}
                      {n.nome_clifor && (
                        <Text as="span" color="#94A3B8" fontWeight={500}>
                          {' · '}
                          {n.nome_clifor}
                        </Text>
                      )}
                    </span>
                  </Tooltip>
                </Celula>
                <Celula muda={muda(n, 'serie')}>{n.serie || '—'}</Celula>
                <Celula muda={muda(n, 'numero')} mono>
                  {n.numero_nota}
                </Celula>
                <Celula muda={muda(n, 'data')}>{fmtData(dataDaNota(n))}</Celula>
                <Celula muda={muda(n, 'valor')} alinhar="right" mono>
                  {provavelCancelada(n) && (
                    <Tooltip
                      label="Lançamento a R$ 0: no Domínio o cancelado vem quase sempre zerado."
                      fontSize="11px"
                      hasArrow
                      placement="top"
                    >
                      <Text
                        as="span"
                        mr="6px"
                        px="5px"
                        py="1px"
                        borderRadius="6px"
                        bg="rgba(239,68,68,0.1)"
                        color="#b91c1c"
                        fontFamily="body"
                        fontSize="9.5px"
                        fontWeight={700}
                        cursor="help"
                      >
                        cancelada?
                      </Text>
                    </Tooltip>
                  )}
                  {fmtValor(valor)}
                </Celula>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

type FiltroMotivo = 'todos' | MotivoDuplicada;

/**
 * Notas duplicadas de uma empresa, em grupos: cada cartão é uma nota lançada
 * mais de uma vez, com todos os lançamentos (inclusive os de fora do mês
 * filtrado) e o que muda entre eles destacado. Usado na tela de duplicadas e
 * no drawer da Conferência — as duas mostram exatamente a mesma coisa.
 */
export const GruposDuplicadas = ({
  notas,
  periodo,
}: {
  notas: INotaDuplicada[];
  periodo?: IPeriodo;
}) => {
  const grupos = useMemo(() => montarGrupos(notas, periodo), [notas, periodo]);
  const resumo = resumoDosGrupos(grupos);
  const [filtro, setFiltro] = useState<FiltroMotivo>('todos');
  const visiveis = filtro === 'todos' ? grupos : grupos.filter(g => g.motivo === filtro);

  const chips: { k: FiltroMotivo; rotulo: string; valor: number; cor: string; bg: string }[] = [
    {
      k: 'todos',
      rotulo: 'Grupos',
      valor: resumo.grupos,
      cor: '#b45309',
      bg: 'rgba(245,158,11,0.1)',
    },
    {
      k: 'valor',
      rotulo: ROTULO_MOTIVO.valor,
      valor: resumo.grupos - resumo.porChave,
      ...COR_MOTIVO.valor,
    },
    { k: 'chave', rotulo: ROTULO_MOTIVO.chave, valor: resumo.porChave, ...COR_MOTIVO.chave },
  ];

  return (
    <Flex direction="column" gap={3}>
      <Flex gap={2} wrap="wrap" align="center">
        {chips.map(c => {
          const ativo = filtro === c.k;
          return (
            <Box
              as="button"
              type="button"
              key={c.k}
              onClick={() => setFiltro(c.k === filtro ? 'todos' : c.k)}
              disabled={c.k !== 'todos' && c.valor === 0}
              display="flex"
              alignItems="center"
              gap="6px"
              px="10px"
              py="5px"
              borderRadius="10px"
              border="1px solid"
              borderColor={ativo ? c.cor : 'rgba(15,23,42,0.08)'}
              bg={ativo ? c.bg : 'white'}
              color={c.cor}
              fontSize="12px"
              fontWeight={600}
              _disabled={{ opacity: 0.45, cursor: 'not-allowed' }}
              transition="border-color 0.15s, background 0.15s"
            >
              <Text as="span" fontWeight={800}>
                {c.valor}
              </Text>
              <Text as="span" color="#475569">
                {c.rotulo}
              </Text>
            </Box>
          );
        })}
        <Text fontSize="11.5px" color="#94A3B8">
          {resumo.lancamentos} lançamentos
          {resumo.canceladaEAtiva > 0 &&
            ` · ${resumo.canceladaEAtiva} com lançamento a R$ 0 e outro com valor`}
          {resumo.comForaDoPeriodo > 0 &&
            ` · ${resumo.comForaDoPeriodo} grupo${
              resumo.comForaDoPeriodo > 1 ? 's' : ''
            } com lançamento em outro mês`}
        </Text>
      </Flex>
      {visiveis.map(g => (
        <CartaoDoGrupo key={g.id} g={g} periodo={periodo} />
      ))}
    </Flex>
  );
};
